/**
 * What M2 adds to the playfield (spec 05 §6.5): the monsters' shots, fire (the falling drops and the
 * burning lines), lightning and red-cross bolts, and the power-ups' world-wide effects - the yellow
 * cross's strikes, the blue cross's flood, the lamps' and the bomb's explosions. Fire, bolts and blasts
 * are self-lit, so they hold their colour in the darkest level. Shots glide on the M-tick clock. Every
 * flicker and bloom goes through the flash cap (flash.ts): with reduce flashing none is faster than
 * 3 Hz and each swings a third as far.
 */

import { BoxGeometry, type BufferGeometry, type Material, Mesh, type Scene, Vector3 } from 'three';
import { boltLooks, fireLook } from '../../sim/bubbles';
import { effectsLook } from '../../sim/items';
import { projectileLook } from '../../sim/monsters';
import type { Projectile, World } from '../../sim/types';
import { bolt, flame } from '../actors/bubbleContents';
import { projectileModel } from '../actors/projectiles';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import { createGlassBubbleMaterial } from '../materials/glassBubble';
import type { Palette } from '../palette';
import type { FigureOptions, Previous } from './entityViews';
import { flicker, softFlash } from './flash';
import { centreToWorld, lerpCoord, pixelToWorld } from './simToWorld';
import { spriteCentre } from '../../sim/coords';

/** A set of meshes sharing one geometry, shown as many at a time as a frame needs. */
class Pool {
  private readonly meshes: Mesh[] = [];
  private used = 0;

  constructor(private readonly scene: Scene, private readonly geometry: BufferGeometry, private readonly material: Material) {}

  begin(): void {
    this.used = 0;
  }

  take(): Mesh {
    let m = this.meshes[this.used];
    if (!m) {
      m = new Mesh(this.geometry, this.material);
      this.scene.add(m);
      this.meshes.push(m);
    }
    this.used++;
    m.visible = true;
    return m;
  }

  end(): void {
    for (let i = this.used; i < this.meshes.length; i++) {
      const m = this.meshes[i];
      if (m) m.visible = false;
    }
  }

  dispose(): void {
    this.scene.remove(...this.meshes);
    this.meshes.length = 0;
  }
}

/** Hz of a flicker written as sin(t * w): w / 2pi. */
const hz = (w: number): number => w / (Math.PI * 2);

export class EffectViews {
  private readonly shots = new Map<number, { mesh: Mesh; kind: string }>();
  private readonly drops: Pool;
  private readonly flames: Pool;
  private readonly bolts: Pool;
  private readonly strikes: Pool;
  private readonly blasts: Pool;
  private readonly flood: Mesh;
  private readonly floodMaterial: Material;
  private readonly tmp = new Vector3();

  options: FigureOptions = { colourAid: false, reduceFlashing: false };
  private readonly geometries: BufferGeometry[];

  constructor(private readonly scene: Scene, private readonly p: Palette, private readonly mats: { shot: Material; glow: Material }) {
    const flameGeo = flame(p).build();
    const boltGeo = bolt(p).build();
    // A fireball: an orange shell round a yellow body and a white-hot core toward the camera.
    const blast = new MeshBuilder()
      .blob(0.5, 0.5, 0.42, p.color('fire.1'), undefined, { detail: 2 })
      .blob(0.36, 0.36, 0.3, p.color('fire.2'), place(0.04, 0.05, 0.18), { detail: 2 })
      .blob(0.18, 0.18, 0.12, p.color('fire.3'), place(0.07, 0.09, 0.38), { detail: 2 })
      .build();
    this.drops = new Pool(scene, flameGeo, mats.glow);
    this.flames = new Pool(scene, flameGeo, mats.glow);
    this.bolts = new Pool(scene, boltGeo, mats.glow);
    this.strikes = new Pool(scene, boltGeo, mats.glow);
    this.blasts = new Pool(scene, blast, mats.glow);
    this.floodMaterial = createGlassBubbleMaterial({ tint: p.color('water.1'), rim: p.color('water.2'), depthBias: 2.5, body: 0.05 });
    this.flood = new Mesh(new BoxGeometry(28, 1, 2.4), this.floodMaterial);
    this.flood.visible = false;
    scene.add(this.flood);
    this.geometries = [flameGeo, boltGeo, blast, this.flood.geometry];
  }

  /** Take everything out of the scene and free what it made (the shot and glow materials are the caller's). */
  dispose(): void {
    this.clear();
    for (const pool of [this.drops, this.flames, this.bolts, this.strikes, this.blasts]) pool.dispose();
    this.scene.remove(this.flood);
    for (const g of this.geometries) g.dispose();
    this.floodMaterial.dispose();
  }

  /** Every material these views draw with (for the warm-up). */
  materials(): Material[] {
    return [this.mats.shot, this.mats.glow, this.floodMaterial];
  }

  private syncShot(s: Projectile, prev: Previous, soft: number, t: number): void {
    let v = this.shots.get(s.id);
    if (!v || v.kind !== s.kind) {
      if (v) this.drop(s.id);
      v = { mesh: new Mesh(projectileModel(s.kind, this.p).build(), this.mats.shot), kind: s.kind };
      this.scene.add(v.mesh);
      this.shots.set(s.id, v);
    }
    // Where the C64 draws it: a 16x16 picture at (x, y), as a bubble; bursting (a rock crumbling)
    // for its last passes, then gone.
    const look = projectileLook(s);
    const before = prev.get(`j${s.id}`) ?? look;
    centreToWorld(lerpCoord(before.x, look.x, soft), lerpCoord(before.y, look.y, soft), this.tmp);
    const m = v.mesh;
    m.visible = look.visible;
    m.position.copy(this.tmp);
    const swell = look.burst < 0 ? 1 : 1.25 + look.burst * 0.35;
    m.scale.set(s.dir * swell, swell * (look.burst < 0 ? 1 : 0.6), swell);
    // A bottle tumbles, a rock rolls, a fireball flickers; the laser holds still.
    m.rotation.z = s.kind === 'bottle' ? t * 12 * -s.dir : s.kind === 'rock' ? t * 8 * -s.dir : 0;
    if (s.kind === 'fireball') m.scale.y *= 1 + flicker(t, hz(30), 0.08, this.options.reduceFlashing);
  }

  private drop(id: number): void {
    const v = this.shots.get(id);
    if (!v) return;
    this.scene.remove(v.mesh);
    v.mesh.geometry.dispose();
    this.shots.delete(id);
  }

  sync(world: World, prev: Previous, soft: number, t: number): void {
    const seen = new Set<number>();
    for (const s of world.projectiles) {
      seen.add(s.id);
      this.syncShot(s, prev, soft, t);
    }
    for (const id of [...this.shots.keys()]) if (!seen.has(id)) this.drop(id);

    const fire = fireLook(world);
    this.drops.begin();
    for (const d of fire.drops) {
      const m = this.drops.take();
      centreToWorld(d.x, d.y, m.position);
      m.scale.setScalar(0.9);
    }
    this.drops.end();
    this.flames.begin();
    fire.cells.forEach((c, i) => {
      const m = this.flames.take();
      pixelToWorld(c.col * 8 + 4, c.row * 8 + 4, m.position);
      // The C64's flame picture: 0 low (catching and dying), 1 and 2 tall.
      const lick = 1 + flicker(t, hz(24), 0.12, this.options.reduceFlashing, i * 1.7);
      m.scale.set(1.1, (c.frame === 0 ? 0.75 : c.frame === 1 ? 1.3 : 1.5) * lick, 1.1);
      m.position.y += c.frame === 0 ? -0.15 : 0.1;
    });
    this.flames.end();

    this.bolts.begin();
    for (const b of boltLooks(world)) {
      const m = this.bolts.take();
      centreToWorld(b.x, b.y, m.position);
      // Lying along its flight, its point leading, crackling.
      m.scale.setScalar(2);
      m.rotation.z = (b.dir > 0 ? -Math.PI / 2 : Math.PI / 2) + flicker(t, hz(40), 0.15, this.options.reduceFlashing);
    }
    this.bolts.end();

    this.syncEffects(world, t);
  }

  private syncEffects(world: World, t: number): void {
    const fx = effectsLook(world);
    const reduce = this.options.reduceFlashing;
    this.strikes.begin();
    for (const s of fx.strikes) {
      const m = this.strikes.take();
      // The strike's 48x48 box, top-left in the VIC frame: its centre.
      pixelToWorld(s.x - 0x14 + 24, s.y - 0x2d + 24, m.position);
      m.scale.setScalar(3.2);
      m.rotation.z = -Math.PI / 4 + flicker(t, hz(50), 0.1, reduce);
    }
    this.strikes.end();
    // The blast (a lamp's six flying pieces, the bomb's pulsing grid): a fireball at each of its
    // sprites, flickering, and a flash at its heart as it starts.
    this.blasts.begin();
    fx.blastPieces.forEach((b, i) => {
      const m = this.blasts.take();
      const c = spriteCentre(b.x, b.y);
      pixelToWorld(c.x, c.y, m.position);
      m.scale.setScalar((b.big ? 3.2 : 1.8) * (1 + flicker(t, hz(28), 0.12, reduce, i)));
    });
    for (const e of fx.explosions) {
      if (e.t >= 0.25) continue;
      const m = this.blasts.take();
      pixelToWorld(e.x - 0x14, e.y - 0x2d, m.position);
      m.scale.setScalar(1 + e.t * softFlash(24, reduce));
    }
    this.blasts.end();
    this.flood.visible = fx.flood > 0;
    if (fx.flood > 0) {
      this.flood.scale.y = fx.flood;
      this.flood.position.set(0, -12.5 + fx.flood / 2, 0.4);
    }
  }

  clear(): void {
    for (const id of [...this.shots.keys()]) this.drop(id);
    this.flood.visible = false;
  }
}
